import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../lib/api';
import { cn, formatDate, formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle,
  AlertCircle,
  Calendar,
  CalendarX,
  Users,
  Clock,
  ChevronRight,
  Loader2,
  Shield,
  CheckCircle2,
  RefreshCcw,
  MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-orange-100 text-orange-700 border-orange-200',
  low: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const RISK_COLORS = {
  weekend: 'bg-amber-100 text-amber-700 border-amber-200',
  no_prep_time: 'bg-orange-100 text-orange-700 border-orange-200',
};

export const ConflictDetection = ({ onEventClick, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState(null);

  useEffect(() => {
    fetchConflicts();
  }, []);

  const fetchConflicts = async () => {
    setLoading(true);
    try {
      const response = await calendarAPI.getConflicts();
      setConflicts(response.data);
    } catch (error) {
      console.error('Failed to fetch conflicts:', error);
      toast.error('Failed to check for conflicts');
    } finally {
      setLoading(false);
    }
  };

  const ConflictItem = ({ conflict }) => (
    <div className={cn(
      "p-3 rounded-lg border",
      SEVERITY_COLORS[conflict.severity] || SEVERITY_COLORS.medium
    )}>
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{conflict.message}</p>
          
          <div className="mt-2 space-y-2">
            {/* Event 1 */}
            <div 
              className="p-2 rounded bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
              onClick={() => onEventClick && onEventClick(conflict.event1.event_id)}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{conflict.event1.title}</span>
                <Badge variant="outline" className="text-xs">
                  {conflict.event1.type.replace('_', ' ')}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs opacity-70">
                <Clock className="h-3 w-3" />
                <span>{formatDateTime(conflict.event1.start)} - {formatDateTime(conflict.event1.end)}</span>
              </div>
            </div>

            {/* Event 2 */}
            <div 
              className="p-2 rounded bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
              onClick={() => onEventClick && onEventClick(conflict.event2.event_id)}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{conflict.event2.title}</span>
                <Badge variant="outline" className="text-xs">
                  {conflict.event2.type.replace('_', ' ')}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs opacity-70">
                <Clock className="h-3 w-3" />
                <span>{formatDateTime(conflict.event2.start)} - {formatDateTime(conflict.event2.end)}</span>
              </div>
            </div>
          </div>

          {conflict.conflicting_people?.length > 0 && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <Users className="h-3 w-3" />
              <span>{conflict.conflicting_people.length} person(s) double-booked</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const HolidayWarning = ({ warning }) => (
    <div className="p-3 rounded-lg border border-purple-200 bg-purple-50">
      <div className="flex items-start gap-3">
        <Calendar className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-sm text-purple-800">{warning.event_title}</p>
          <p className="text-xs text-purple-600 mt-1">{warning.warning}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs bg-purple-100 border-purple-300 text-purple-700">
              {warning.holiday_jurisdiction}
            </Badge>
            <span className="text-xs text-purple-600">{formatDate(warning.date)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const DeadlineRisk = ({ risk }) => (
    <div className={cn(
      "p-3 rounded-lg border",
      RISK_COLORS[risk.risk] || RISK_COLORS.weekend
    )}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-sm">{risk.title}</p>
          <p className="text-xs mt-1">{risk.message}</p>
          {risk.suggestion && (
            <p className="text-xs mt-2 italic opacity-80">
              Suggestion: {risk.suggestion}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {risk.risk.replace('_', ' ')}
            </Badge>
            <span className="text-xs opacity-70">{formatDate(risk.date)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!conflicts) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Unable to check conflicts
        </CardContent>
      </Card>
    );
  }

  const { conflicts: conflictList, holiday_warnings, deadline_risks, total_conflicts, total_warnings } = conflicts;
  const hasIssues = total_conflicts > 0 || total_warnings > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className={cn("h-5 w-5", hasIssues ? "text-amber-500" : "text-green-500")} />
            <CardTitle>Conflict Detection</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchConflicts}>
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>
          Scheduling conflicts and deadline risks
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className={cn(
            "text-center p-2 rounded-lg border",
            total_conflicts > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
          )}>
            <p className={cn("text-xl font-bold", total_conflicts > 0 ? "text-red-600" : "text-green-600")}>
              {total_conflicts}
            </p>
            <p className={cn("text-xs", total_conflicts > 0 ? "text-red-600" : "text-green-600")}>
              Conflicts
            </p>
          </div>
          <div className={cn(
            "text-center p-2 rounded-lg border",
            holiday_warnings.length > 0 ? "bg-purple-50 border-purple-200" : "bg-green-50 border-green-200"
          )}>
            <p className={cn("text-xl font-bold", holiday_warnings.length > 0 ? "text-purple-600" : "text-green-600")}>
              {holiday_warnings.length}
            </p>
            <p className={cn("text-xs", holiday_warnings.length > 0 ? "text-purple-600" : "text-green-600")}>
              Holidays
            </p>
          </div>
          <div className={cn(
            "text-center p-2 rounded-lg border",
            deadline_risks.length > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"
          )}>
            <p className={cn("text-xl font-bold", deadline_risks.length > 0 ? "text-amber-600" : "text-green-600")}>
              {deadline_risks.length}
            </p>
            <p className={cn("text-xs", deadline_risks.length > 0 ? "text-amber-600" : "text-green-600")}>
              Risks
            </p>
          </div>
        </div>

        <ScrollArea className="h-[350px]">
          {!hasIssues ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium text-green-700">No Conflicts Detected</p>
              <p className="text-sm text-green-600 mt-1">Your calendar is clear of scheduling issues</p>
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={['conflicts', 'holidays', 'risks']} className="space-y-2">
              {/* Conflicts */}
              {conflictList.length > 0 && (
                <AccordionItem value="conflicts" className="border rounded-lg border-red-200">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <CalendarX className="h-4 w-4 text-red-500" />
                      <span className="font-medium text-red-700">
                        Time Conflicts ({conflictList.length})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <div className="space-y-2">
                      {conflictList.map((conflict, idx) => (
                        <ConflictItem key={idx} conflict={conflict} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Holiday Warnings */}
              {holiday_warnings.length > 0 && (
                <AccordionItem value="holidays" className="border rounded-lg border-purple-200">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      <span className="font-medium text-purple-700">
                        Holiday Warnings ({holiday_warnings.length})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <div className="space-y-2">
                      {holiday_warnings.map((warning, idx) => (
                        <HolidayWarning key={idx} warning={warning} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Deadline Risks */}
              {deadline_risks.length > 0 && (
                <AccordionItem value="risks" className="border rounded-lg border-amber-200">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-amber-700">
                        Deadline Risks ({deadline_risks.length})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <div className="space-y-2">
                      {deadline_risks.map((risk, idx) => (
                        <DeadlineRisk key={idx} risk={risk} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default ConflictDetection;
